/**
 * Public plan catalogue, the one source the landing and pricing pages share.
 *
 * Numbers match docs/strategy.md. The worker maps paid plan names to Stripe
 * price ids server-side; the client never sees a price_ id.
 */

/**
 * Every price on the site is this currency, stated rather than implied.
 *
 * "$9" alone is ambiguous, the business is in Malaysia and the dollar sign is
 * shared by several currencies a buyer might reasonably assume. Stripe's review
 * looks for an unambiguous price, and so does anyone deciding whether to pay.
 */
export const PLAN_CURRENCY = 'USD';

export type PaidPlanId = 'starter' | 'growth' | 'scale';

export type PlanId = 'free' | PaidPlanId;

export type PlanTier = {
    id: PlanId;
    name: string;
    priceLabel: string;
    priceDetail: string;
    requests: string;
    blurb: string;
    /** Highlighted column on the pricing table. */
    featured?: boolean;
};

export const PLAN_TIERS: readonly PlanTier[] = [
    {
        id: 'free',
        name: 'Free',
        priceLabel: '$0',
        priceDetail: 'forever',
        requests: '100 requests / mo',
        // Not "evaluate", which implies the tier is a window that closes. It
        // renews monthly and permits commercial use, same as the paid plans.
        blurb: 'Small tools and side projects. Commercial use allowed. No card.',
    },
    {
        id: 'starter',
        name: 'Starter',
        priceLabel: '$9',
        priceDetail: 'per month',
        requests: '2,000 requests / mo',
        blurb: 'For a tool or a small production integration.',
        featured: true,
    },
    {
        id: 'growth',
        name: 'Growth',
        priceLabel: '$29',
        priceDetail: 'per month',
        requests: '10,000 requests / mo',
        blurb: 'Volume for a product that runs sold comps regularly.',
    },
    {
        id: 'scale',
        name: 'Scale',
        priceLabel: '$79',
        priceDetail: 'per month',
        requests: '50,000 requests / mo',
        blurb: 'Higher throughput for broader coverage.',
    },
] as const;

export function isPaidPlanId(value: string): value is PaidPlanId {
    return value === 'starter' || value === 'growth' || value === 'scale';
}

/**
 * The one way a price is written on this site.
 *
 * `priceLabel` and `priceDetail` are fragments, and every page that joined them
 * itself picked a different joiner, the landing page rendered "$9 / per month"
 * and "$0 / forever" while the pricing page rendered "$9 per month". A reader
 * comparing the two pages sees two different offers written two different ways
 * and has no way to know they are the same numbers. Join them here instead, so
 * there is exactly one answer.
 */
export function formatPlanPrice(tier: PlanTier): string {
    return `${tier.priceLabel} ${tier.priceDetail}`;
}

/**
 * `PLAN_TIERS` is a literal, so none of these lookups can miss at runtime, but
 * the throw is what keeps that true. Deleting a tier from the catalogue should
 * fail loudly at import rather than quietly render "undefined" in the hero.
 */
function requireTier(predicate: (tier: PlanTier) => boolean, label: string): PlanTier {
    const tier = PLAN_TIERS.find(predicate);
    if (!tier) throw new Error(`PLAN_TIERS has no ${label} tier`);
    return tier;
}

export const FREE_TIER: PlanTier = requireTier((tier) => tier.id === 'free', 'free');

/** Cheapest tier that costs money, what "paid plans from …" refers to. */
export const CHEAPEST_PAID_TIER: PlanTier = requireTier(
    (tier) => isPaidPlanId(tier.id),
    'paid',
);

